import React from 'react';
import styled, { css } from 'styled-components';

import { useModal } from '@tager/web-components';

import { colors } from '@/constants/theme';
import DetailedCarModal from '@/components/modals/DetailedCarModal';
import { useTypedSelector } from '@/store/store';
import { selectDetailedCarData } from '@/store/reducers/detailed-car';
import {
  getDealersOptions,
  getEventParams,
} from '@/modules/DetailedCar/DetailedCar.helper';
import useSettingItem from '@/hooks/useSettingItem';
import CallbackForm from '@/components/modals/CallbackForm';
import { googleEvent } from '@/utils/events';
import defaultStockCar from '@/assets/images/cars-stock/defaultStockCar.png';
import { media } from '@/utils/mixin';
import { CallbackFormFields } from '@/components/modals/CallbackForm/CallbackForm.types';
import { submitDetailedCarConsultation } from '@/services/leads/leads-service';
import { redirect } from '@/utils/common';
import { StyledOverlay } from '@/components/modals/shared/StyledOverlay';

function ApplicationButtons() {
  const data = useTypedSelector(selectDetailedCarData);
  const openModal = useModal();
  const isDisabledPurchase = useSettingItem('PURCHASE_DISABLED');
  const isOnlineTestDrive = useSettingItem('DETAILED_CAR_ONLINE_TEST_DRIVE');
  const consultationGoogleEvent = useSettingItem(
    'DETAILED_CAR_NEW_CONSULTATION_GOOGLE_EVENT'
  );

  if (!data || data.actionsDisabled) {
    return null;
  }

  const {
    id,
    onlinePurchase,
    pricing,
    testDrive,
    catalog: { brand, model },
    params: { body },
    media: { images },
  } = data;

  const isPurchaseButtonAvailable =
    onlinePurchase.available && onlinePurchase.subdomain && !isDisabledPurchase;

  async function handleSubmitCallbackForm(
    values: CallbackFormFields
  ): Promise<void> {
    if (!data) {
      return;
    }

    try {
      await submitDetailedCarConsultation({
        ...values,
        id,
        dealer: Number(values.dealer?.value),
      }).then(() => {
        if (consultationGoogleEvent) {
          googleEvent(consultationGoogleEvent, getEventParams(data));
        }
      });
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  function handleDetailedCarModal(
    type?: 'APPLICATION' | 'TEST_DRIVE' | 'TEST_DRIVE_OFFLINE'
  ) {
    if (!data) {
      return;
    }

    if (type) {
      openModal(
        DetailedCarModal,
        {
          type,
          typeState: 'new',
          car: {
            id,
            name: `${brand.name} ${model?.name ?? ''}`,
            price: pricing?.discountPrice ?? pricing?.price ?? undefined,
            bodyType: body?.label ?? '',
            image: { url: images.length ? images[0] : defaultStockCar },
            dealers: getDealersOptions(data),
            googleEventParams: getEventParams(data),
          },
        },
        { scrollLockDisabled: true, components: { Overlay: StyledOverlay } }
      );
    } else {
      openModal(
        CallbackForm,
        {
          request: handleSubmitCallbackForm,
          title: 'Получить консультацию',
          fieldsConfig: {
            withMessage: true,
            dealers: getDealersOptions(data),
          },
        },
        { scrollLockDisabled: true, components: { Overlay: StyledOverlay } }
      );
    }
  }

  function handleBuyOnline() {
    if (data && onlinePurchase.available && onlinePurchase.subdomain) {
      redirect(
        `https://${onlinePurchase.subdomain}.${window.location.host}/cars/new/${id}/purchase`,
        { hardReload: true }
      );
    } else {
      alert('No Order Link');
    }
  }

  return (
    <Component>
      <OrderButton onClick={() => handleDetailedCarModal('APPLICATION')}>
        Получить предложение
      </OrderButton>

      <SecondButtonsWrapper>
        {testDrive.available && (
          <SecondButton
            onClick={() =>
              handleDetailedCarModal(
                isOnlineTestDrive ? 'TEST_DRIVE' : 'TEST_DRIVE_OFFLINE'
              )
            }
          >
            Тест-драйв
          </SecondButton>
        )}

        {isPurchaseButtonAvailable ? (
          <SecondButton onClick={handleBuyOnline}>Забронировать</SecondButton>
        ) : (
          <SecondButton onClick={() => handleDetailedCarModal()}>
            Консультация
          </SecondButton>
        )}
      </SecondButtonsWrapper>
    </Component>
  );
}

export default ApplicationButtons;

const Component = styled.div`
  margin-top: 30px;

  ${media.laptopOnly(css`
    margin-top: 15px;
  `)}
`;

const Button = styled.button`
  font-size: 17px;
  text-transform: uppercase;
  font-weight: 500;
  padding: 10px 0;
  border-radius: 30px;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;

  ${media.laptopOnly(css`
    font-size: 15px;
    padding: 8px 0;
  `)}
`;

const OrderButton = styled(Button)`
  width: 100%;
  background-color: ${colors.main};
  border: 1px solid ${colors.main};
  color: ${colors.white};

  &:hover {
    background-color: ${colors.main100};
  }
`;

const SecondButtonsWrapper = styled.div`
  display: flex;
  margin-top: 10px;
  justify-content: space-between;

  ${media.mobile(css`
    display: block;
  `)}
`;

const SecondButton = styled(Button)`
  width: 100%;
  color: ${(props) => props.theme.main};
  border: 1px solid ${(props) => props.theme.main};

  &:not(:first-child) {
    margin-left: 16px;

    ${media.mobile(css`
      margin: 10px 0 0;
    `)}
  }

  &:hover {
    color: ${colors.white};
    background-color: ${colors.main100};
    border-color: ${colors.main100};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;

    &:hover {
      background-color: ${(props) => props.theme.white};
      color: ${(props) => props.theme.main};
      border-color: ${(props) => props.theme.main};
    }
  }
`;
